import type { IScene, SceneInitData } from './IScene';
import { gameLogger } from '../../utils/GameLogger';

/**
 * SceneRegistry — substrate-agnostic scene state machine.
 *
 * Phase H10 Arc λ.9 Stage B4 (2026-05-09). Replaces Phaser.Game's scene
 * manager for gameplay scenes. Pre-Stage-B4, scenes extended Phaser.Scene
 * and Phaser auto-bound init/preload/create/update/shutdown lifecycle
 * methods. With this registry, lifecycle is driven manually:
 *
 *   1. add(key, ctor) registers a scene class.
 *   2. start(key, data) instantiates → calls init(data) → calls preload()
 *      → awaits create() → marks scene active. From then on each update()
 *      tick is forwarded to the active scene.
 *   3. stop(key) calls shutdown() on the scene.
 *   4. restart(data) is stop(active) + start(active, data).
 *
 * Concurrency model: only one active scene at a time (matches gameplay
 * convention). Multi-scene overlay is out-of-scope; cutscenes/menus run
 * via React UI overlays, not parallel scenes.
 */

type SceneCtor = new () => IScene;

export class SceneRegistry {
  private readonly registered = new Map<string, SceneCtor>();
  private active: IScene | null = null;
  private activeKey: string | null = null;

  /** Register a scene class under a stable key. Idempotent. */
  add(key: string, ctor: SceneCtor): void {
    if (this.registered.has(key)) {
      gameLogger.debug('scene', `[SceneRegistry] Scene "${key}" already registered, skipping.`);
      return;
    }
    this.registered.set(key, ctor);
    gameLogger.debug('scene', `[SceneRegistry] Registered scene "${key}"`);
  }

  /** Start a registered scene. Stops the current active scene first if any.
   *  Returns a promise that resolves once the target scene's create() has
   *  finished (so consumers can await full readiness). */
  async start(key: string, data?: SceneInitData): Promise<void> {
    const ctor = this.registered.get(key);
    if (!ctor) {
      gameLogger.error('scene', `[SceneRegistry] Unknown scene key: "${key}"`);
      return;
    }

    if (this.active && this.activeKey) {
      this.stop(this.activeKey);
    }

    const scene = new ctor();
    this.active = scene;
    this.activeKey = key;

    try {
      scene.init?.(data ?? {});
      // preload + create may be async; await both.
      const preloadResult = (scene as IScene & { preload?: () => void | Promise<void> }).preload?.();
      if (preloadResult && typeof (preloadResult as Promise<void>).then === 'function') {
        await preloadResult;
      }
      const createResult = scene.create?.();
      if (createResult && typeof (createResult as Promise<void>).then === 'function') {
        await createResult;
      }
      gameLogger.info('scene', `[SceneRegistry] Started scene "${key}"`);
    } catch (err) {
      gameLogger.error('scene', `[SceneRegistry] Error starting scene "${key}"`, err);
    }
  }

  /** Stop a scene by key. Calls shutdown() on the scene if it's currently
   *  active; clears active reference. No-op if the scene isn't active. */
  stop(key: string): void {
    if (this.activeKey !== key || !this.active) return;
    try {
      this.active.shutdown?.();
    } catch (err) {
      gameLogger.error('scene', `[SceneRegistry] Error stopping scene "${key}"`, err);
    }
    gameLogger.info('scene', `[SceneRegistry] Stopped scene "${key}"`);
    this.active = null;
    this.activeKey = null;
  }

  /** Restart the currently-active scene with new data. Equivalent to
   *  stop(active) + start(active, data). */
  async restart(data?: SceneInitData): Promise<void> {
    if (!this.activeKey) {
      gameLogger.warn('scene', '[SceneRegistry] restart() called with no active scene; ignoring.');
      return;
    }
    const key = this.activeKey;
    await this.start(key, data);
  }

  /** True if the named scene is currently the active scene. */
  isActive(key: string): boolean {
    return this.activeKey === key && this.active !== null;
  }

  /** Per-frame tick — forwarded to the active scene's update() if defined.
   *  GameHost's rAF loop calls this each frame. */
  tick(time: number, delta: number): void {
    this.active?.update?.(time, delta);
  }

  /** Inspector — get the currently active scene (or null). Used by
   *  diagnostics + the IGameHost.scenes facade for cross-system reads. */
  getActive(): IScene | null {
    return this.active;
  }

  getActiveKey(): string | null {
    return this.activeKey;
  }

  /** Stop everything + clear the registry. Called by GameHost.destroy. */
  destroy(): void {
    if (this.activeKey) this.stop(this.activeKey);
    this.registered.clear();
  }
}
